import React, { useEffect } from "react";

// components
import { ButtonStandard } from "@performyard/py-components";

// utils
import { type IQuery, type IQueryField, calculateIsQueryComplete, getDefaultComparator } from "./QueryBuilderUtils";
import SingleQueryInput from "./components/SingleQueryInput";

// hooks
import { useListState } from "../../hooks/useListState/useListState";

import Spacer from "../Spacer";
// css
import css from "./QueryBuilder.module.scss";
import { DEFAULT_QUERY_OPTIONS } from "./constants";

const INITIAL_LIST_STATE = [{ comparator: DEFAULT_QUERY_OPTIONS[0] }];

declare interface QueryBuilderProps {
	selectionFields?: IQueryField[];
	addCondition: (orQueryCondition: Array<any>) => void;
	editOrQuery?: IQuery[];
	canQueryOnMultipleFields?: boolean;
	enableAnniversary?: boolean;
	fieldSelectPlaceholder?: string;
}

/*
 * The QueryBuilder component is used to build a query
 * It handles it's own query state for OR statements, then uses addCondition to pass the query to the parent component
 * then it clears the current query state
 */
const QueryBuilder = ({
	selectionFields = [],
	addCondition,
	editOrQuery,
	canQueryOnMultipleFields = true,
	enableAnniversary = false,
	fieldSelectPlaceholder,
}: QueryBuilderProps) => {
	const {
		listWithUniqueIds: currentQuery,
		list: currentQueryForSave,
		setList: setCurrentQuery,
		append: appendCurrentQuery,
		remove: removeCurrentQuery,
		update: updateCurrentQuery,
		clear: clearCurrentQuery,
	} = useListState(INITIAL_LIST_STATE);

	useEffect(() => {
		if (editOrQuery) {
			setCurrentQuery(editOrQuery);
		} else {
			setCurrentQuery(INITIAL_LIST_STATE);
		}
	}, [editOrQuery]);

	const handleAddCondition = () => {
		addCondition(currentQueryForSave);
		clearCurrentQuery();
	};

	const handleAddOrCondition = () => {
		if (canQueryOnMultipleFields) {
			appendCurrentQuery({ comparator: DEFAULT_QUERY_OPTIONS[0] });
		} else {
			const field = currentQuery[0].state.field;
			const comparator = getDefaultComparator(field?.type || "", true);
			appendCurrentQuery({ comparator, field });
		}
	};

	const handleUpdateQuery = async (index: number, newQueryValue: any) => {
		// this allows the query to reset all or conditions if the employee field is changed and multiple employee fields are not allowed
		if (
			!canQueryOnMultipleFields &&
			index === 0 &&
			currentQuery.length > 1 &&
			newQueryValue.field !== currentQuery[0].state.field
		) {
			setCurrentQuery([newQueryValue]);
		} else {
			updateCurrentQuery(index, newQueryValue);
		}
	};

	return (
		<div className={css.QueryBuilder} data-testid="query-builder">
			{currentQuery.map(({ state: query, uniqueListId }, index) => (
				<div className={css.queries} key={`${uniqueListId}-single-query`} id={`singleQueryInput-${index}`}>
					<SingleQueryInput
						selections={selectionFields}
						isEdit={!!editOrQuery}
						query={query as IQuery}
						disableEmployeeFieldSelector={!canQueryOnMultipleFields && index > 0}
						isLastQuery={index === currentQuery.length - 1}
						onChange={(newQueryValue) => handleUpdateQuery(index, newQueryValue)}
						addOrCondition={() => handleAddOrCondition()}
						removeCondition={() => {
							if (currentQuery.length > 1) {
								removeCurrentQuery(index);
							} else {
								clearCurrentQuery();
							}
						}}
						enabledAnniversary={enableAnniversary}
						fieldSelectPlaceholder={fieldSelectPlaceholder}
					/>
					<Spacer height={2} />
				</div>
			))}
			<ButtonStandard onClick={handleAddCondition} disabled={!calculateIsQueryComplete(currentQueryForSave)}>
				{editOrQuery ? "Update Condition" : "Add Condition"}
			</ButtonStandard>
		</div>
	);
};

export default QueryBuilder;
